<script>
import Multiselect from "vue-multiselect";
import {
  FETCH_RECON_REPORT_FILTERS,
  FETCH_RECON_SCOPED_ACCESS_USERS,
  SAVE_RECON_SCOPED_ACCESS_RULES,
} from "@/state/actions.type";

const ALL_MARKETPLACE_NAME = "*";

export default {
  components: {
    Multiselect,
  },

  data() {
    return {
      loading: false,
      processing: false,
      submitAttempted: false,
      selectedUser: null,
      savedRules: [],
      workingRules: [],
      effectOptions: ["allow", "deny"],
    };
  },

  computed: {
    users() {
      return (this.$store.state.reconScopedAccess.users || []).filter(
        (user) => !(user.roles || []).includes("admin")
      );
    },
    dataAccessEnabled() {
      return this.$store.state.reconScopedAccess.dataAccessEnabled;
    },
    marketplaces() {
      return this.$store.state.reconReports.selection.marketplaces || [];
    },
    // Marketplace dropdown options, with a synthetic "All" entry pinned first.
    marketplaceOptions() {
      return [{ name: ALL_MARKETPLACE_NAME, brands: [] }, ...this.marketplaces];
    },
  },

  watch: {
    selectedUser(newUser) {
      this.loadRulesForUser(newUser);
    },
  },

  created() {
    this.loading = true;
    const promises = [];

    if (!this.marketplaces || this.marketplaces.length === 0) {
      promises.push(
        this.$store.dispatch(`reconReports/${FETCH_RECON_REPORT_FILTERS}`)
      );
    }
    promises.push(
      this.$store.dispatch(`reconScopedAccess/${FETCH_RECON_SCOPED_ACCESS_USERS}`)
    );

    Promise.all(promises).finally(() => {
      this.loading = false;
    });
  },

  methods: {
    subUserLabel(user) {
      return `${user.name} (${user.email_address})`;
    },
    effectLabel(value) {
      if (value === "deny") return "Deny";
      if (value === "allow") return "Allow";
      return "Effect";
    },
    allLabel(value) {
      return value === ALL_MARKETPLACE_NAME ? "All" : value;
    },
    displayList(values) {
      return values && values.length ? values.map((v) => this.allLabel(v)).join(", ") : "-";
    },
    isAllMarketplace(row) {
      return !!(row.marketplace && row.marketplace.name === ALL_MARKETPLACE_NAME);
    },
    findMarketplaceByName(name) {
      if (name === ALL_MARKETPLACE_NAME) {
        return { name: ALL_MARKETPLACE_NAME, brands: [] };
      }
      return this.marketplaces.find((m) => m.name === name) || null;
    },
    brandOptionsForRow(row) {
      if (!row.marketplace || this.isAllMarketplace(row)) return [];
      const names = (row.marketplace.brands || []).map((b) => b.name);
      return ["*", ...new Set(names)];
    },
    countryOptionsForRow(row) {
      if (!row.marketplace || this.isAllMarketplace(row)) return [];
      const names = [];
      (row.marketplace.brands || []).forEach((brand) => {
        (brand.countries || []).forEach((country) => names.push(country.name));
      });
      return ["*", ...new Set(names)];
    },
    onMarketplaceChange(row) {
      if (this.isAllMarketplace(row)) {
        // "All" marketplace implies "All" country/brand — nothing else to pick.
        row.countries = ["*"];
        row.brands = ["*"];
      } else {
        row.countries = [];
        row.brands = [];
      }
    },
    // Multi-select "All" is exclusive: picking "All" clears everything else,
    // and picking a specific value drops "All" if it was selected.
    sanitizeMultiSelection(newValues, oldValues) {
      const hadAll = (oldValues || []).includes(ALL_MARKETPLACE_NAME);
      const hasAll = (newValues || []).includes(ALL_MARKETPLACE_NAME);
      if (hasAll && !hadAll) {
        return [ALL_MARKETPLACE_NAME];
      }
      if (hasAll && newValues.length > 1) {
        return newValues.filter((v) => v !== ALL_MARKETPLACE_NAME);
      }
      return newValues;
    },
    onCountriesChange(row, newValues) {
      row.countries = this.sanitizeMultiSelection(newValues, row.countries);
    },
    onBrandsChange(row, newValues) {
      row.brands = this.sanitizeMultiSelection(newValues, row.brands);
    },
    // Groups flat rule objects (as returned by the API) into UI rows keyed by
    // (marketplace, effect), collecting each group's distinct countries/brands
    // into multi-select arrays. This is the inverse of buildPayloadRules()'s
    // expansion — round-trips exactly when the source data forms a clean
    // marketplace+effect rectangle of countries x brands.
    mapRulesToRows(rules) {
      const rows = [];
      const groups = new Map();

      (rules || []).forEach((rule) => {
        // No marketplace, or marketplace "*", means "All marketplaces" — always
        // its own row (country/brand are implicitly "All" and never combined
        // with other rules).
        if (!rule.marketplace || rule.marketplace === ALL_MARKETPLACE_NAME) {
          rows.push({
            marketplace: { name: ALL_MARKETPLACE_NAME, brands: [] },
            countries: ["*"],
            brands: ["*"],
            effect: rule.effect || null,
            readonly: false,
            _rawMarketplace: null,
          });
          return;
        }

        const marketplace = this.findMarketplaceByName(rule.marketplace);
        const key = `${marketplace ? "known" : "unknown"}||${rule.marketplace}||${rule.effect}`;

        if (!groups.has(key)) {
          groups.set(key, {
            marketplaceObj: marketplace,
            rawMarketplace: rule.marketplace,
            effect: rule.effect,
            countries: new Set(),
            brands: new Set(),
          });
        }
        const group = groups.get(key);
        if (rule.country) group.countries.add(rule.country);
        if (rule.brand) group.brands.add(rule.brand);
      });

      groups.forEach((group) => {
        rows.push({
          marketplace: group.marketplaceObj || null,
          countries: [...group.countries],
          brands: [...group.brands],
          effect: group.effect || null,
          readonly: !group.marketplaceObj,
          _rawMarketplace: group.marketplaceObj ? null : group.rawMarketplace,
        });
      });

      return rows;
    },
    loadRulesForUser(user) {
      this.submitAttempted = false;
      if (!user) {
        this.savedRules = [];
        this.workingRules = [];
        return;
      }
      this.savedRules = JSON.parse(JSON.stringify(user.rules || []));
      this.workingRules = this.mapRulesToRows(this.savedRules);
    },
    addRule() {
      this.workingRules.push({
        marketplace: null,
        countries: [],
        brands: [],
        effect: null,
        readonly: false,
        _rawMarketplace: null,
      });
    },
    removeRule(index) {
      this.workingRules.splice(index, 1);
    },
    cancelRules() {
      this.workingRules = this.mapRulesToRows(this.savedRules);
      this.submitAttempted = false;
    },
    validateRules() {
      return this.workingRules.every(
        (row) => row.readonly || (row.marketplace && row.effect)
      );
    },
    // Expands each row's multi-selected Countries/Brands into individual
    // {marketplace, country, brand, effect} objects (cartesian product), so the
    // payload structure stays exactly the flat, single-value-per-field shape
    // the backend expects. A dimension left empty means "no constraint" and is
    // omitted from every generated object, rather than being treated as zero
    // combinations.
    buildPayloadRules() {
      const result = [];

      this.workingRules.forEach((row) => {
        // "All" marketplace: only effect is sent, no marketplace/country/brand.
        if (this.isAllMarketplace(row)) {
          result.push({ effect: row.effect });
          return;
        }

        const marketplaceValue = row.marketplace
          ? row.marketplace.name
          : row._rawMarketplace;
        const countries = row.countries && row.countries.length ? row.countries : [null];
        const brands = row.brands && row.brands.length ? row.brands : [null];

        countries.forEach((country) => {
          brands.forEach((brand) => {
            const rule = { effect: row.effect, marketplace: marketplaceValue };
            if (country) rule.country = country;
            if (brand) rule.brand = brand;
            result.push(rule);
          });
        });
      });

      return result;
    },
    saveRules() {
      this.submitAttempted = true;
      if (!this.validateRules()) return;

      this.processing = true;
      const rules = this.buildPayloadRules();
      const subuserUuid = this.selectedUser.uuid;

      this.$store
        .dispatch(`reconScopedAccess/${SAVE_RECON_SCOPED_ACCESS_RULES}`, {
          subuser_uuid: subuserUuid,
          rules,
        })
        .then(() => {
          const refreshedUser = this.users.find((u) => u.uuid === subuserUuid);
          if (refreshedUser) {
            this.selectedUser = refreshedUser;
          }
          this.$notify({
            type: "success",
            text: "Changes Saved Successfully",
            closeOnClick: true,
          });
        })
        .catch((error) => {
          console.log("error", error);
        })
        .finally(() => {
          this.processing = false;
        });
    },
  },
};
</script>

<template>
  <div>
    <div class="toggle-settings" v-if="loading">
      <p class="section-text m-0">Loading...</p>
    </div>

    <div class="toggle-settings" v-else-if="!dataAccessEnabled">
      <p class="section-title m-0">Recon Scoped Access</p>
      <p class="section-text mt-2 mb-0">
        Data access control is not enabled for this account.
      </p>
    </div>

    <div class="toggle-settings" v-else>
      <p class="section-title m-0">Recon Scoped Access</p>
      <p class="section-text mt-2 mb-0">
        Select a sub-user to view and manage which marketplaces, countries
        and brands they can access reconciliation data for.
      </p>

      <div class="mt-3" style="max-width: 420px;">
        <multiselect
          v-model="selectedUser"
          :options="users"
          track-by="uuid"
          :custom-label="subUserLabel"
          :placeholder="$t('Select a sub-user')"
          :searchable="true"
          class="custom-multiselect"
        ></multiselect>
      </div>

      <div v-if="selectedUser" class="mt-4">
        <hr style="border-color: rgba(220, 218, 213, 0.30);" />

        <div class="my-3" v-for="(row, index) in workingRules" :key="index">
          <div
            v-if="row.readonly"
            class="d-flex align-items-center p-3"
            style="gap: 24px; border-radius: 8px; background: rgba(220, 218, 213, 0.15);"
          >
            <div>
              <div class="text-muted font-12px">Marketplace</div>
              <div>{{ allLabel(row._rawMarketplace) }}</div>
            </div>
            <div>
              <div class="text-muted font-12px">Country</div>
              <div>{{ displayList(row.countries) }}</div>
            </div>
            <div>
              <div class="text-muted font-12px">Brand</div>
              <div>{{ displayList(row.brands) }}</div>
            </div>
            <div>
              <div class="text-muted font-12px">Effect</div>
              <div>{{ effectLabel(row.effect) }}</div>
            </div>
            <b-btn variant="link" class="ml-auto my-auto p-0" @click="removeRule(index)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 12L12 4M12 12L4 4" stroke="#E36464" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </b-btn>
          </div>

          <div v-else class="d-flex" style="gap: 12px;">
            <div style="width: 220px;">
              <multiselect
                v-model="row.marketplace"
                :options="marketplaceOptions"
                track-by="name"
                :custom-label="(m) => allLabel(m.name)"
                :placeholder="$t('Marketplace')"
                :searchable="true"
                :class="{ 'is-invalid': submitAttempted && !row.marketplace }"
                @input="onMarketplaceChange(row)"
              ></multiselect>
              <div
                class="mt-1 font-12px text-danger"
                v-if="submitAttempted && !row.marketplace"
              >
                This field is required.
              </div>
            </div>
            <div style="width: 220px;">
              <multiselect
                :value="row.countries"
                @input="(val) => onCountriesChange(row, val)"
                :options="countryOptionsForRow(row)"
                :custom-label="allLabel"
                :multiple="true"
                :close-on-select="false"
                :placeholder="$t('Country')"
                :searchable="true"
                :disabled="!row.marketplace || isAllMarketplace(row)"
                class="currency-multiselect"
              >
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <b-form-checkbox disabled :checked="row.countries.includes(props.option)">
                      <span class="font-14px" style="line-height: 20px; color: #000;">{{ allLabel(props.option) }}</span>
                    </b-form-checkbox>
                  </div>
                </template>
              </multiselect>
            </div>
            <div style="width: 220px;">
              <multiselect
                :value="row.brands"
                @input="(val) => onBrandsChange(row, val)"
                :options="brandOptionsForRow(row)"
                :custom-label="allLabel"
                :multiple="true"
                :close-on-select="false"
                :placeholder="$t('Brand')"
                :searchable="true"
                :disabled="!row.marketplace || isAllMarketplace(row)"
                class="currency-multiselect"
              >
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <b-form-checkbox disabled :checked="row.brands.includes(props.option)">
                      <span class="font-14px" style="line-height: 20px; color: #000;">{{ allLabel(props.option) }}</span>
                    </b-form-checkbox>
                  </div>
                </template>
              </multiselect>
            </div>
            <div style="width: 160px;">
              <multiselect
                v-model="row.effect"
                :options="effectOptions"
                :custom-label="effectLabel"
                :placeholder="$t('Effect')"
                :class="{ 'is-invalid': submitAttempted && !row.effect }"
              ></multiselect>
              <div
                class="mt-1 font-12px text-danger"
                v-if="submitAttempted && !row.effect"
              >
                This field is required.
              </div>
            </div>

            <b-btn variant="link" class="my-auto p-0" @click="removeRule(index)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 12L12 4M12 12L4 4" stroke="#E36464" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </b-btn>
          </div>
        </div>

        <b-btn class="text-chai p-0 mt-2" variant="link" @click="addRule">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 12H18M12 18V6" stroke="#FC6B2D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Add Rule
        </b-btn>

        <div class="mt-4">
          <b-btn variant="primary" :disabled="processing" @click="saveRules">Save</b-btn>
          <b-btn variant="cancel" :disabled="processing" class="ml-2" @click="cancelRules">Cancel</b-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.toggle-settings {
  margin-top: 24px;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #dcdad5;
}
.section-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}
.section-text {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}
::v-deep .custom-control-label::before {
  border-color: #000 !important;
}
::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  border-color: #000 !important;
  background-color: #000 !important;
}
::v-deep .custom-checkbox .custom-control-input:checked ~ .custom-control-label::after {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.5 3.625L5.3125 9.8125L2.5 7" stroke="white" stroke-width="1.6666" stroke-linecap="round" stroke-linejoin="round"/></svg>');
}
::v-deep .currency-multiselect .multiselect__option--highlight,
::v-deep .currency-multiselect .multiselect__option--highlight:after {
  background: rgba(220, 218, 213, 0.30) !important;
  color: #000 !important;
}
::v-deep .currency-multiselect .multiselect__option--selected::after,
::v-deep .currency-multiselect .multiselect__option--highlight::after {
  display: none !important;
}
::v-deep .currency-multiselect .multiselect__option {
  min-height: auto;
  padding: 0;
}
.option__desc {
  padding: 12px;
}
::v-deep.is-invalid > .multiselect__tags {
  border-color: #ff3d60 !important;
}
::v-deep .currency-multiselect.multiselect--active > .multiselect__tags {
  border: 1px solid rgba(252, 107, 45, 0.70) !important;
  box-shadow: 0px 0px 0px 4px rgba(248, 88, 19, 0.10) !important;
}
::v-deep .multiselect__input::placeholder {
  color: rgba(0, 0, 0, 0.3) !important;
  font-weight: 500 !important;
}
</style>
